import type { AnyModelSpec } from '@elara/firebolt/shared';

/**
 * Creates a lightweight, schema-validated local model that mirrors the API of Firestore-backed models
 * but without any Firestore I/O. Useful for transient data sourced from APIs.
 *
 * `Context` is optional caller-supplied data the extensions may need but the document itself doesn't
 * carry — e.g. a room rate needs its stay dates to resolve a "2 days before arrival" cancellation
 * deadline, and those live on the parent RoomOption's search, not on the rate. Extensions are computed
 * eagerly at construction (the returned model is a plain object of values, not live getters), so context
 * has to arrive here rather than being attached afterwards.
 */
export function createLocalModel<Spec extends AnyModelSpec, Extensions, Context = undefined>(
  spec: Spec,
  makeExtensions: (data: Spec['DataType'], context?: Context) => Extensions,
): {
  spec: Spec;
  from: (input: Spec['InputType'], context?: Context) => Spec['DataType'] & Extensions;
  fromMany: (inputs?: Array<Spec['InputType']>, context?: Context) => Array<Spec['DataType'] & Extensions>;
} {
  function from(input: Spec['InputType'], context?: Context) {
    // Strip createdAt and updatedAt from input for transient models
    // These fields are added by defineModelSpec but aren't relevant for external API data
    const inputObj = input as Record<string, unknown>;
    const inputWithoutTimestamps = { ...inputObj };
    delete inputWithoutTimestamps.createdAt;
    delete inputWithoutTimestamps.updatedAt;

    const parsed = spec.schema.safeParse(inputWithoutTimestamps);
    if (!parsed.success) {
      throw new Error(`Invalid ${spec.name}: ${JSON.stringify(parsed.error.issues)}`);
    }

    const data = spec.read(parsed.data);
    return { ...data, ...makeExtensions(data, context) } as Spec['DataType'] & Extensions;
  }

  function fromMany(inputs: Array<Spec['InputType']> = [], context?: Context) {
    return inputs.map((input) => from(input, context));
  }

  return {
    spec,
    from,
    fromMany,
  };
}
